import { useState } from 'react';
import { queryService } from '../../../services/queryService';
import api from '../../../services/api';
import { useSnackbar } from '../../../components/common/Snackbar';
import { normalizeId, canEscalate } from '../../../utils/allQueriesUtils';
import { normalizeLifecycleStatus } from '../../../utils/queryListUtils';

/**
 * Hook to manage query operations for AllQueries page
 * @param {Function} fetchQueries - Function to refetch queries
 * @returns {Object} Operation handlers and state
 */
export const useAllQueriesPageOperations = (fetchQueries) => {
  const { showSnackbar } = useSnackbar();
  const [escalatingId, setEscalatingId] = useState(null);

  const performManualEscalate = async (query, escalationData = {}) => {
    if (!query?._id || !canEscalate(query)) return;
    setEscalatingId(query._id);
    try {
      const normalizedCurrentStatus = normalizeLifecycleStatus(query.status);
      const normalizedLabelStatus = normalizeLifecycleStatus(query.statusLabel);

      const description =
        (query.statusResponse || '').trim() ||
        'Manually escalated from All Queries table.';

      const escalationTargetCandidates = [
        normalizeId(query.escalatedTo),
        normalizeId(query.assignee),
      ];
      const fallbackTargetId = escalationTargetCandidates.find(Boolean) || null;

      const selectedPath = Array.isArray(escalationData.escalationPath)
        ? escalationData.escalationPath
        : [];
      const primaryTargetId =
        escalationData.primaryTargetId ||
        (selectedPath.length > 0 ? selectedPath[0].userId : null) ||
        fallbackTargetId;

      const escalationSummary =
        selectedPath.length > 0
          ? `Escalation path: ${selectedPath
              .map((item) => `${item.roleLabel || item.role} - ${item.userName}`)
              .join(' -> ')}`
          : null;

      const regionSummary = escalationData.selectedRegion
        ? `Region: ${escalationData.selectedRegion}`
        : null;

      const descriptionParts = [description, regionSummary, escalationSummary].filter(Boolean);
      const finalDescription = descriptionParts.join(' | ');

      if (!primaryTargetId) {
        showSnackbar('No eligible escalation target found. Please assign the query first.', 'error');
        setEscalatingId(null);
        return;
      }

      if (
        normalizedCurrentStatus === 'escalated' &&
        normalizedLabelStatus !== 'escalated'
      ) {
        await queryService.updateQueryStatus(query._id, {
          status: 'raised',
          description: 'Resetting lifecycle before manual escalation',
        });
      }

      await queryService.updateQueryStatus(query._id, {
        status: 'escalated',
        description: finalDescription,
        escalatedToId: primaryTargetId || undefined,
        region: escalationData.selectedRegion || undefined,
      });
      showSnackbar('Query escalated and notifications triggered', 'success');
      fetchQueries();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to escalate query';
      showSnackbar(message, 'error');
    } finally {
      setEscalatingId(null);
    }
  };

  const confirmDelete = async (query) => {
    if (!query) return;
    try {
      await api.delete(`/queries/${query._id}`);
      showSnackbar('Query deleted successfully', 'success');
      fetchQueries();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete query';
      showSnackbar(message, 'error');
    }
  };

  return {
    escalatingId,
    setEscalatingId,
    performManualEscalate,
    confirmDelete,
  };
};
